<template>
  <div class="checklist">
    <div v-if="entries.length" class="checklist__summary">
      <div class="checklist__line">
        <span>{{ summary }}</span>
      </div>
      <v-progress-linear :model-value="percent" rounded height="6" color="accent" bg-color="surface-variant" bg-opacity="0.4" />
    </div>

    <KEmptyState v-if="!entries.length" title="Aucune tâche en cours" description="Tout est à jour." icon="mdi-check-circle-outline" />

    <ul v-else class="checklist__list">
      <li v-for="entry in entries" :key="entry.key" :class="entry.heading ? 'checklist__heading' : 'checklist__row'">
        <template v-if="entry.heading">{{ entry.heading }}</template>
        <template v-else>
          <button type="button" class="checklist__toggle" :aria-label="toggleLabel(entry.task)" @click="advance(entry.task)">
            <svg v-if="entry.state === 'todo'" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9.5" class="checklist__ring" stroke-width="2" />
            </svg>
            <svg v-else-if="entry.state === 'doing'" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9.5" class="checklist__ring checklist__ring--doing" stroke-width="2" />
              <path d="M12 5.5a6.5 6.5 0 0 1 0 13z" class="checklist__half" />
            </svg>
            <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="10.5" class="checklist__done" />
              <path d="M7.5 12.4l3 3 6-6.4" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <div class="checklist__text">
            <span class="checklist__title" :class="{ 'checklist__title--done': entry.state === 'done' }">{{ entry.task.task }}</span>
            <div class="checklist__meta">
              <span class="checklist__due" :class="{ 'checklist__due--late': entry.late, 'checklist__due--doing': entry.state === 'doing' }">{{ entry.due }}</span>
              <KChip v-if="entry.project" :label="entry.project" color="info" />
              <KChip :label="entry.priority" :color="entry.priorityColor" />
              <KChip :label="entry.category" color="accent" />
            </div>
          </div>
          <div v-if="entry.people.length" class="checklist__people">
            <v-tooltip v-for="person in entry.people" :key="person.id" location="top">
              <template v-slot:activator="{ props }">
                <v-avatar v-bind="props" size="26" class="checklist__avatar" :color="person.image ? undefined : 'primary'" variant="tonal" tabindex="0" :aria-label="person.name">
                  <v-img v-if="person.image" cover :src="person.image" />
                  <span v-else class="checklist__initials">{{ person.initials }}</span>
                </v-avatar>
              </template>
              {{ person.name }}
            </v-tooltip>
          </div>
          <v-btn
            v-if="entry.state === 'doing'"
            icon
            size="small"
            variant="tonal"
            color="warning"
            :aria-label="`Remettre en attente : ${entry.task.task}`"
            @click="pause(entry.task)"
          >
            <v-icon icon="mdi-pause" size="18" />
            <v-tooltip activator="parent" location="top">Remettre en attente</v-tooltip>
          </v-btn>
        </template>
      </li>
    </ul>

    <label class="checklist__add">
      <v-icon icon="mdi-plus" size="18" color="accent" />
      <input
        v-model="draft"
        type="text"
        class="checklist__input"
        placeholder="Ajouter une tâche, puis Entrée"
        aria-label="Ajouter une tâche"
        :disabled="creating"
        @keydown.enter.prevent="create"
      >
    </label>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import KEmptyState from "@/components/ui/KEmptyState";
import KChip from "@/components/ui/KChip";
import { useTasksStore, dueMs } from "@/stores/tasks";
import { apiUser } from "@/components/toLoad/api";
import { setSnackbar } from "@/stores/ui";

const DAY = 86400000;
const PRIORITY_COLORS = ["secondary", "info", "primary", "warning", "error"];
const NEXT = { 0: 2, 1: 2, 2: 3, 3: 1 };

export default {
  name: "TasksChecklist",
  components: { KEmptyState, KChip },
  props: {
    userInfo: {
      type: Object,
      required: true,
    },
    limit: {
      type: Number,
      default: 8,
    },
  },
  data() {
    return {
      finishedIds: [],
      draft: "",
      creating: false,
    };
  },
  computed: {
    ...mapStores(useTasksStore),
    tasks() {
      const open = this.tasksStore.list.filter((task) => Number(task.status) < 3);
      const openIds = new Set(open.map((task) => task.id));
      const finished = this.finishedIds
        .filter((id) => !openIds.has(id))
        .map((id) => this.tasksStore.byId[id])
        .filter(Boolean);
      return [...open, ...finished].sort((a, b) => dueMs(a.dueDate) - dueMs(b.dueDate));
    },
    urgent() {
      const limit = Date.now() + 7 * DAY;
      return this.tasks.filter((task) => Number(task.status) === 2 || dueMs(task.dueDate) <= limit);
    },
    later() {
      const urgentIds = new Set(this.urgent.map((task) => task.id));
      return this.tasks.filter((task) => !urgentIds.has(task.id));
    },
    entries() {
      const entries = [];
      const push = (list, heading) => {
        list.forEach((task, index) => {
          if (index === 0 && this.urgent.length && this.later.length) entries.push({ key: `heading-${heading}`, heading });
          entries.push({
            key: task.id,
            task,
            state: this.state(task),
            late: this.isLate(task),
            due: this.dueLabel(task),
            project: task.projectData?.title || "",
            priority: this.$listUtils.priority[Number(task.priority)] || "",
            priorityColor: PRIORITY_COLORS[Number(task.priority)] || "info",
            category: this.$listUtils.itemCategory[Number(task.category)]?.label || "",
            people: (task.participantsData || []).slice(0, 3).map((person) => this.personEntry(person)),
          });
        });
      };
      push(this.urgent, "À faire en priorité");
      push(this.later, "Plus tard");
      return entries.slice(0, this.limit);
    },
    doneCount() {
      return this.tasks.filter((task) => this.state(task) === "done").length;
    },
    percent() {
      return this.tasks.length ? Math.round((this.doneCount / this.tasks.length) * 100) : 0;
    },
    summary() {
      const late = this.tasks.filter((task) => this.isLate(task)).length;
      const base = `${this.doneCount} sur ${this.tasks.length} terminées`;
      return late ? `${base} · ${late} en retard` : base;
    },
  },
  watch: {
    "userInfo.company": {
      handler(company) {
        if (company) this.tasksStore.ensureWatch(company);
      },
      immediate: true,
    },
  },
  methods: {
    personEntry(person) {
      const name = person.firstName ? `${person.firstName} ${person.lastName || ""}`.trim() : person.name || "";
      return {
        id: person.id,
        name,
        image: person.avatar ? this.$functions.getImgLink(person.avatar, "small") : "",
        initials: this.$functions.getInitials(name || "?"),
      };
    },
    state(task) {
      const status = Number(task.status);
      if (status === 3) return "done";
      if (status === 2) return "doing";
      return "todo";
    },
    toggleLabel(task) {
      const labels = { todo: "Commencer la tâche", doing: "Terminer la tâche", done: "Rouvrir la tâche" };
      return `${labels[this.state(task)]} : ${task.task}`;
    },
    isLate(task) {
      return this.state(task) !== "done" && dueMs(task.dueDate) < Date.now();
    },
    dueLabel(task) {
      const due = dueMs(task.dueDate);
      if (this.state(task) === "doing" && due === Infinity) return "En cours";
      if (due === Infinity) return "Sans échéance";
      if (this.isLate(task)) return `Retard · ${Math.max(1, Math.round((Date.now() - due) / DAY))} j`;
      const date = new Date(due);
      if (date.toDateString() === new Date().toDateString())
        return `Aujourd'hui ${new Intl.DateTimeFormat("fr-FR", { hour: "2-digit", minute: "2-digit" }).format(date)}`;

      return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short" }).format(date);
    },
    async setStatus(task, status) {
      if (status === 3) this.finishedIds = [...this.finishedIds, task.id];
      else this.finishedIds = this.finishedIds.filter((id) => id !== task.id);
      const done = await this.tasksStore.setStatus(task, status);
      if (!done) this.finishedIds = this.finishedIds.filter((id) => id !== task.id);
    },
    advance(task) {
      return this.setStatus(task, NEXT[Number(task.status)] ?? 2);
    },
    pause(task) {
      return this.setStatus(task, 1);
    },
    async create() {
      const text = this.draft.trim();
      if (!text || this.creating || !this.userInfo.company) return;

      this.creating = true;
      try {
        await apiUser({
          method: "post",
          url: `/companies/${this.userInfo.company}/tasks`,
          data: {
            task: text,
            status: 1,
            category: 0,
            priority: 2,
            content: "",
            creationDate: new Date(),
            lastUpdate: new Date(),
            author: this.userInfo.id,
          },
        });
        this.draft = "";
        setSnackbar({ text: `La tâche ${text} a été ajoutée !` });
      } catch {
        setSnackbar({ text: "Impossible d'ajouter la tâche.", color: "error" });
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<style scoped>
.checklist {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.checklist__summary {
  padding: 0 4px 8px;
}

.checklist__line {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 12.5px;
  opacity: 0.7;
}

.checklist__list {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.checklist__heading {
  padding: 10px 4px 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-accent));
}

.checklist__row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  border-top: 1px solid var(--k-glass-border);
}

.checklist__toggle {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0 -6px 0 -10px;
  padding: 0;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 999px;
  transition: transform var(--k-dur-fast) var(--k-ease-out);
}

.checklist__toggle:hover {
  transform: scale(1.08);
}

.checklist__ring {
  stroke: color-mix(in srgb, currentColor 28%, transparent);
}

.checklist__ring--doing {
  stroke: rgb(var(--v-theme-info));
}

.checklist__half {
  fill: rgb(var(--v-theme-info));
}

.checklist__done {
  fill: rgb(var(--v-theme-success));
}

.checklist__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.checklist__title {
  overflow: hidden;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checklist__title--done {
  text-decoration: line-through;
  opacity: 0.5;
}

.checklist__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 6px;
}

.checklist__people {
  display: flex;
  flex: none;
  align-items: center;
}

.checklist__avatar {
  margin-inline-start: -8px;
  border: 2px solid var(--k-glass-3-bg);
}

.checklist__avatar:first-child {
  margin-inline-start: 0;
}

.checklist__initials {
  font-size: 10px;
  font-weight: 600;
}

.checklist__due {
  font-size: 12px;
  opacity: 0.65;
}

.checklist__due--doing {
  color: rgb(var(--v-theme-info));
  opacity: 1;
}

.checklist__due--late {
  font-weight: 500;
  color: rgb(var(--v-theme-error));
  opacity: 1;
}

.checklist__add {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  border-top: 1px solid var(--k-glass-border);
}

.checklist__input {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: inherit;
  background: transparent;
  border: 0;
  outline: 0;
}

.checklist__input::placeholder {
  color: inherit;
  opacity: 0.5;
}
</style>
